import { useEffect, useState, useCallback, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import DashboardLayout from '@/components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { ClipboardList, UserPlus, Pencil, Trash2, Search, X, Save, Truck, MessageSquare, Send, GraduationCap, Copy } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUSES = ['NEW', 'CONTACTED', 'INTERVIEWED', 'AWAITING_DOCS', 'SENT_TO_DPD', 'HIRED', 'AWAITING_TRAINING', 'REJECTED', 'DECLINED'];
const STATUS_LABEL = {
  NEW: 'NEW',
  CONTACTED: 'CONTACTED',
  INTERVIEWED: 'INTERVIEWED',
  AWAITING_DOCS: 'AWAITING DOCS',
  SENT_TO_DPD: 'SENT TO DPD',
  HIRED: 'HIRED',
  AWAITING_TRAINING: 'AWAITING TRAINING',
  REJECTED: 'REJECTED',
  DECLINED: 'DECLINED',
};
const STATUS_COLOR = {
  NEW:               'bg-blue-100 text-blue-800 border-blue-300',
  CONTACTED:         'bg-amber-100 text-amber-800 border-amber-300',
  INTERVIEWED:       'bg-violet-100 text-violet-800 border-violet-300',
  AWAITING_DOCS:     'bg-yellow-100 text-yellow-800 border-yellow-300',
  SENT_TO_DPD:       'bg-cyan-100 text-cyan-800 border-cyan-300',
  HIRED:             'bg-emerald-100 text-emerald-800 border-emerald-300',
  AWAITING_TRAINING: 'bg-teal-100 text-teal-800 border-teal-300',
  REJECTED:          'bg-rose-100 text-rose-800 border-rose-300',
  DECLINED:          'bg-rose-200 text-rose-900 border-rose-400',
};

const blankForm = (type) => ({
  type,
  first_name: '',
  surname: '',
  phone: '',
  address: '',
  experience: '',
  vehicle_type: '',
  ready_for_training: false,
  applying_for: type === 'owner_driver' ? 'Owner Driver' : 'Driver',
  notes: '',
});

const APPLYING_FOR_BADGE = {
  'Owner Driver': 'bg-amber-100 text-amber-800 border-amber-300',
  'Driver':       'bg-blue-100 text-blue-800 border-blue-300',
  'Assistant':    'bg-violet-100 text-violet-800 border-violet-300',
  'Porter':       'bg-orange-100 text-orange-800 border-orange-300',
};

export default function ApplicationsPage() {
  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  const [activeType, setActiveType] = useState('driver');
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [rows, setRows] = useState([]);
  const [counts, setCounts] = useState({ driver: {}, owner_driver: {}, total_new: 0 });
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');

  // form
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(blankForm('driver'));
  const [saving, setSaving] = useState(false);

  // status change
  const [statusChange, setStatusChange] = useState(null);
  const [statusNote, setStatusNote] = useState('');

  // comments thread
  const [commentsApp, setCommentsApp] = useState(null);
  const [newComment, setNewComment] = useState('');
  const [postingComment, setPostingComment] = useState(false);

  // onboard
  const [onboardApp, setOnboardApp] = useState(null);
  const [onboardForm, setOnboardForm] = useState({ username: '', password: '' });
  const [onboardSaving, setOnboardSaving] = useState(false);
  const [onboardResult, setOnboardResult] = useState(null);

  const role = useMemo(() => {
    try { return JSON.parse(localStorage.getItem('user') || '{}').role; } catch { return ''; }
  }, []);
  const me = useMemo(() => {
    try { return JSON.parse(localStorage.getItem('user') || '{}'); } catch { return {}; }
  }, []);
  const isAdmin = role === 'admin';

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = { type: activeType };
      if (statusFilter !== 'ALL') params.status = statusFilter;
      const [listRes, countsRes] = await Promise.all([
        axios.get(`${API}/applications`, { headers, params }),
        axios.get(`${API}/applications/counts`, { headers }),
      ]);
      setRows(listRes.data || []);
      setCounts(countsRes.data || {});
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load applications');
    } finally { setLoading(false); }
  }, [activeType, statusFilter]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // ---- handlers ----
  const openAdd = () => { setEditing({}); setForm(blankForm(activeType)); };
  const openEdit = (a) => {
    setEditing(a);
    setForm({
      type: a.type,
      first_name: a.first_name || '',
      surname: a.surname || '',
      phone: a.phone || '',
      address: a.address || '',
      experience: a.experience || '',
      vehicle_type: a.vehicle_type || '',
      ready_for_training: !!a.ready_for_training,
      applying_for: a.applying_for || (a.type === 'owner_driver' ? 'Owner Driver' : 'Driver'),
      notes: a.notes || '',
    });
  };
  const closeForm = () => { setEditing(null); };

  const save = async () => {
    if (!form.first_name.trim() || !form.surname.trim()) { toast.error('First name and surname are required'); return; }
    setSaving(true);
    try {
      const payload = { ...form };
      if (editing?.id) {
        await axios.put(`${API}/applications/${editing.id}`, payload, { headers });
        toast.success('Application updated');
      } else {
        await axios.post(`${API}/applications`, payload, { headers });
        toast.success('Application captured');
      }
      closeForm();
      await fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Save failed');
    } finally { setSaving(false); }
  };

  const remove = async (a) => {
    if (!window.confirm(`Delete application for ${a.first_name} ${a.surname}?`)) return;
    try {
      await axios.delete(`${API}/applications/${a.id}`, { headers });
      toast.success('Deleted');
      await fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Delete failed');
    }
  };

  const changeStatus = async () => {
    if (!statusChange) return;
    try {
      await axios.put(`${API}/applications/${statusChange.app.id}/status`,
        { status: statusChange.to, note: statusNote }, { headers });
      toast.success(`Marked ${statusChange.to}`);
      setStatusChange(null); setStatusNote('');
      await fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Status change failed');
    }
  };

  // ---- comments ----
  const openComments = (a) => { setCommentsApp(a); setNewComment(''); };
  const refreshCommentsApp = async () => {
    if (!commentsApp) return;
    try {
      const r = await axios.get(`${API}/applications`, { headers, params: { type: commentsApp.type } });
      const fresh = (r.data || []).find(x => x.id === commentsApp.id);
      if (fresh) setCommentsApp(fresh);
      setRows(prev => prev.map(p => p.id === fresh?.id ? fresh : p));
    } catch { /* silent */ }
  };
  const postComment = async () => {
    if (!commentsApp) return;
    const text = newComment.trim();
    if (!text) return;
    setPostingComment(true);
    try {
      await axios.post(`${API}/applications/${commentsApp.id}/comments`, { text }, { headers });
      setNewComment('');
      await refreshCommentsApp();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to post comment');
    } finally { setPostingComment(false); }
  };
  const removeComment = async (commentId) => {
    if (!commentsApp) return;
    if (!window.confirm('Delete this comment?')) return;
    try {
      await axios.delete(`${API}/applications/${commentsApp.id}/comments/${commentId}`, { headers });
      await refreshCommentsApp();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Delete failed');
    }
  };

  // ---- onboard ----
  const openOnboard = (a) => {
    setOnboardApp(a);
    // auto-suggested username (lowercase, name.surname)
    const suggested = `${(a.first_name || '').toLowerCase()}.${(a.surname || '').toLowerCase()}`.replace(/[^a-z0-9.]/g, '');
    setOnboardForm({ username: suggested, password: '' });
    setOnboardResult(null);
  };
  const submitOnboard = async () => {
    if (!onboardApp) return;
    setOnboardSaving(true);
    try {
      const res = await axios.post(`${API}/applications/${onboardApp.id}/onboard`,
        { username: onboardForm.username.trim() || null, password: onboardForm.password.trim() || null },
        { headers }
      );
      setOnboardResult(res.data);
      toast.success('Driver onboarded — login credentials ready to share');
      await fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Onboard failed');
    } finally { setOnboardSaving(false); }
  };
  const copyToClipboard = (text) => {
    navigator.clipboard?.writeText(text).then(
      () => toast.success('Copied'),
      () => toast.error('Copy failed'),
    );
  };

  // search filter (client-side)
  const visibleRows = useMemo(() => {
    if (!search.trim()) return rows;
    const q = search.toLowerCase();
    return rows.filter(r =>
      `${r.first_name} ${r.surname}`.toLowerCase().includes(q) ||
      (r.phone || '').toLowerCase().includes(q) ||
      (r.address || '').toLowerCase().includes(q) ||
      (r.vehicle_type || '').toLowerCase().includes(q)
    );
  }, [rows, search]);

  const typeCounts = counts[activeType] || {};
  const totalForActive = Object.values(typeCounts).reduce((a, b) => a + (b || 0), 0);

  return (
    <DashboardLayout>
      <div className="space-y-4 p-2 sm:p-4">
        <Card className="border-slate-200">
          <CardHeader className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div>
              <CardTitle className="flex items-center gap-2 text-slate-900">
                <ClipboardList className="h-6 w-6 text-blue-600" /> New Applications
              </CardTitle>
              <p className="text-xs text-muted-foreground mt-1">
                Capture prospective drivers and owner-drivers. Track each application through
                <span className="font-mono"> NEW → CONTACTED → INTERVIEWED → HIRED / REJECTED</span>.
              </p>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <div className="relative">
                <Search className="h-4 w-4 absolute left-2 top-3 text-muted-foreground" />
                <Input
                  placeholder="Search name / phone / address"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-8 w-64"
                  data-testid="applications-search"
                />
              </div>
              <Button onClick={openAdd} className="bg-emerald-600 hover:bg-emerald-700" data-testid="add-application-btn">
                <UserPlus className="h-4 w-4 mr-1" /> Add Application
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <Tabs value={activeType} onValueChange={(v) => { setActiveType(v); setStatusFilter('ALL'); }}>
              <TabsList className="bg-slate-100 mb-3">
                <TabsTrigger value="driver" className="data-[state=active]:bg-blue-600 data-[state=active]:text-white" data-testid="tab-driver">
                  Drivers
                  {(counts.driver?.NEW || 0) > 0 && <span className="ml-2 text-[10px] bg-blue-600 text-white rounded-full px-1.5 min-w-[18px] h-[18px] inline-flex items-center justify-center">{counts.driver.NEW}</span>}
                </TabsTrigger>
                <TabsTrigger value="owner_driver" className="data-[state=active]:bg-amber-600 data-[state=active]:text-white" data-testid="tab-owner-driver">
                  <Truck className="h-3 w-3 mr-1" /> Owner Drivers
                  {(counts.owner_driver?.NEW || 0) > 0 && <span className="ml-2 text-[10px] bg-amber-600 text-white rounded-full px-1.5 min-w-[18px] h-[18px] inline-flex items-center justify-center">{counts.owner_driver.NEW}</span>}
                </TabsTrigger>
              </TabsList>

              {/* status filter chips */}
              <div className="flex flex-wrap gap-1.5 mb-3" data-testid="status-filter">
                {['ALL', ...STATUSES].map(s => (
                  <button
                    key={s}
                    onClick={() => setStatusFilter(s)}
                    className={`px-2.5 py-1 text-xs rounded border transition ${
                      statusFilter === s
                        ? 'bg-slate-900 text-white border-slate-900'
                        : 'bg-white text-slate-700 border-slate-300 hover:bg-slate-50'
                    }`}
                    data-testid={`status-filter-${s}`}
                  >
                    {s === 'ALL' ? 'ALL' : (STATUS_LABEL[s] || s)} {s !== 'ALL' && <span className="opacity-70 ml-1">({typeCounts[s] || 0})</span>}
                    {s === 'ALL' && <span className="opacity-70 ml-1">({totalForActive})</span>}
                  </button>
                ))}
              </div>

              <TabsContent value="driver" className="mt-0">
                <ApplicationTable
                  loading={loading}
                  rows={visibleRows}
                  type="driver"
                  onEdit={openEdit}
                  onDelete={remove}
                  onStatusChange={(app, to) => { setStatusChange({ app, to }); setStatusNote(''); }}
                  onComments={openComments}
                  onOnboard={openOnboard}
                  isAdmin={isAdmin}
                />
              </TabsContent>
              <TabsContent value="owner_driver" className="mt-0">
                <ApplicationTable
                  loading={loading}
                  rows={visibleRows}
                  type="owner_driver"
                  onEdit={openEdit}
                  onDelete={remove}
                  onStatusChange={(app, to) => { setStatusChange({ app, to }); setStatusNote(''); }}
                  onComments={openComments}
                  onOnboard={openOnboard}
                  isAdmin={isAdmin}
                />
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>

        {/* ===== ADD / EDIT DIALOG ===== */}
        <Dialog open={editing !== null} onOpenChange={(o) => !o && closeForm()}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                {editing?.id ? <Pencil className="h-5 w-5 text-blue-600" /> : <UserPlus className="h-5 w-5 text-emerald-600" />}
                {editing?.id ? 'Edit Application' : 'New Application'}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-3">
              <div>
                <Label>Application Type *</Label>
                <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v, applying_for: v === 'owner_driver' ? 'Owner Driver' : (form.applying_for === 'Owner Driver' ? 'Driver' : (form.applying_for || 'Driver')) })}>
                  <SelectTrigger data-testid="form-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="driver">Driver</SelectItem>
                    <SelectItem value="owner_driver">Owner Driver</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {form.type === 'driver' && (
                <div>
                  <Label>Applying For *</Label>
                  <Select value={form.applying_for || 'Driver'} onValueChange={(v) => setForm({ ...form, applying_for: v })}>
                    <SelectTrigger data-testid="form-applying-for"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Driver">Driver</SelectItem>
                      <SelectItem value="Assistant">Assistant</SelectItem>
                      <SelectItem value="Porter">Porter</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>First Name *</Label>
                  <Input value={form.first_name} onChange={e => setForm({ ...form, first_name: e.target.value })} data-testid="form-first-name" />
                </div>
                <div>
                  <Label>Surname *</Label>
                  <Input value={form.surname} onChange={e => setForm({ ...form, surname: e.target.value })} data-testid="form-surname" />
                </div>
              </div>
              <div>
                <Label>Phone <span className="text-xs text-muted-foreground">(optional)</span></Label>
                <Input value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} placeholder="071 234 5678" data-testid="form-phone" />
              </div>

              {form.type === 'driver' && (
                <>
                  <div>
                    <Label>Address</Label>
                    <Textarea rows={2} value={form.address} onChange={e => setForm({ ...form, address: e.target.value })} placeholder="Street, suburb, city" data-testid="form-address" />
                  </div>
                  <div>
                    <Label>Experience</Label>
                    <Textarea rows={2} value={form.experience} onChange={e => setForm({ ...form, experience: e.target.value })} placeholder="e.g. 3 years long-haul, Code 14, hazchem" data-testid="form-experience" />
                  </div>
                </>
              )}

              {form.type === 'owner_driver' && (
                <>
                  <div>
                    <Label>Vehicle Type</Label>
                    <Input value={form.vehicle_type} onChange={e => setForm({ ...form, vehicle_type: e.target.value })} placeholder="e.g. NP200, 1-ton bakkie, 4-ton truck" data-testid="form-vehicle-type" />
                  </div>
                  <div className="flex items-center gap-2 pt-2">
                    <Checkbox
                      id="ready-train"
                      checked={form.ready_for_training}
                      onCheckedChange={(c) => setForm({ ...form, ready_for_training: !!c })}
                      data-testid="form-ready-training"
                    />
                    <Label htmlFor="ready-train" className="cursor-pointer">Ready to go for training</Label>
                  </div>
                </>
              )}

              <div>
                <Label>Notes <span className="text-xs text-muted-foreground">(optional)</span></Label>
                <Textarea rows={2} value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} data-testid="form-notes" />
              </div>

              <div className="flex justify-end gap-2 pt-2">
                <Button variant="outline" onClick={closeForm}><X className="h-4 w-4 mr-1" /> Cancel</Button>
                <Button onClick={save} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700" data-testid="save-application-btn">
                  <Save className="h-4 w-4 mr-1" /> {saving ? 'Saving…' : (editing?.id ? 'Update' : 'Capture')}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* ===== STATUS CHANGE DIALOG ===== */}
        <Dialog open={statusChange !== null} onOpenChange={(o) => !o && setStatusChange(null)}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>
                Move to <span className={`ml-1 px-2 py-0.5 rounded text-xs font-bold border ${STATUS_COLOR[statusChange?.to] || ''}`}>{STATUS_LABEL[statusChange?.to] || statusChange?.to}</span>
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-3">
              <p className="text-sm text-muted-foreground">
                {statusChange?.app?.first_name} {statusChange?.app?.surname}
              </p>
              <div>
                <Label>Note <span className="text-xs text-muted-foreground">(optional)</span></Label>
                <Textarea rows={3} value={statusNote} onChange={e => setStatusNote(e.target.value)} placeholder="e.g. spoke on 12/02, scheduled interview Friday" data-testid="status-change-note" />
              </div>
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setStatusChange(null)}>Cancel</Button>
                <Button onClick={changeStatus} className="bg-slate-900 hover:bg-slate-800 text-white" data-testid="confirm-status-change-btn">Confirm</Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
        {/* ===== ONBOARD AS DRIVER DIALOG ===== */}
        <Dialog open={onboardApp !== null} onOpenChange={(o) => !o && (setOnboardApp(null), setOnboardResult(null))}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-teal-700">
                <GraduationCap className="h-5 w-5" />
                {onboardResult ? 'Driver Onboarded' : 'Onboard as Driver'}
              </DialogTitle>
            </DialogHeader>
            {!onboardResult ? (
              <div className="space-y-3">
                <p className="text-sm text-slate-700">
                  Creating a login for <b>{onboardApp?.first_name} {onboardApp?.surname}</b> as a driver. Their application status will move to <b>AWAITING TRAINING</b>.
                </p>
                <div>
                  <Label>Username <span className="text-xs text-muted-foreground">(auto-suggested)</span></Label>
                  <Input
                    value={onboardForm.username}
                    onChange={e => setOnboardForm({ ...onboardForm, username: e.target.value.toLowerCase().replace(/[^a-z0-9.]/g, '') })}
                    placeholder="john.doe"
                    className="font-mono"
                    data-testid="onboard-username"
                  />
                </div>
                <div>
                  <Label>Password <span className="text-xs text-muted-foreground">(leave blank to use the default)</span></Label>
                  <Input
                    value={onboardForm.password}
                    onChange={e => setOnboardForm({ ...onboardForm, password: e.target.value })}
                    placeholder="Default driver password"
                    className="font-mono"
                    data-testid="onboard-password"
                  />
                </div>
                <div className="flex justify-end gap-2 pt-2">
                  <Button variant="outline" onClick={() => setOnboardApp(null)}>Cancel</Button>
                  <Button
                    onClick={submitOnboard}
                    disabled={onboardSaving}
                    className="bg-teal-600 hover:bg-teal-700 text-white"
                    data-testid="confirm-onboard-btn"
                  >
                    <GraduationCap className="h-4 w-4 mr-1" /> {onboardSaving ? 'Creating…' : 'Create Login & Set Awaiting Training'}
                  </Button>
                </div>
              </div>
            ) : (
              <div className="space-y-3">
                <p className="text-sm text-emerald-700 font-medium">
                  Share these credentials with the driver. Status is now <b>AWAITING TRAINING</b>.
                </p>
                <div className="rounded-md border border-teal-200 bg-teal-50 p-3 space-y-2" data-testid="onboard-result">
                  <CredentialRow label="Full Name" value={onboardResult.full_name} onCopy={copyToClipboard} />
                  <CredentialRow label="Username" value={onboardResult.username} onCopy={copyToClipboard} mono />
                  <CredentialRow label="Password" value={onboardResult.password} onCopy={copyToClipboard} mono />
                </div>
                <p className="text-[11px] text-muted-foreground">
                  Tip: the driver can change their own password in their profile after first login.
                </p>
                <div className="flex justify-end pt-2">
                  <Button onClick={() => { setOnboardApp(null); setOnboardResult(null); }} className="bg-slate-900 text-white">Done</Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* ===== COMMENTS DIALOG ===== */}
        <Dialog open={commentsApp !== null} onOpenChange={(o) => !o && setCommentsApp(null)}>
          <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <MessageSquare className="h-5 w-5 text-emerald-600" />
                Comments — {commentsApp?.first_name} {commentsApp?.surname}
              </DialogTitle>
              <p className="text-xs text-muted-foreground pt-1">
                Ongoing log. Status changes (with their notes) are kept separately in the application&apos;s status history.
              </p>
            </DialogHeader>
            <div className="space-y-3">
              {/* New comment composer */}
              <div className="border rounded-md p-2 bg-slate-50">
                <Textarea
                  rows={2}
                  value={newComment}
                  onChange={(e) => setNewComment(e.target.value)}
                  placeholder="Add a comment… (e.g. &apos;sent training schedule&apos;, &apos;no answer on phone twice&apos;)"
                  className="bg-white"
                  data-testid="new-comment-input"
                />
                <div className="flex justify-end pt-2">
                  <Button
                    onClick={postComment}
                    disabled={postingComment || !newComment.trim()}
                    className="bg-emerald-600 hover:bg-emerald-700"
                    data-testid="post-comment-btn"
                  >
                    <Send className="h-3 w-3 mr-1" /> {postingComment ? 'Posting…' : 'Post Comment'}
                  </Button>
                </div>
              </div>

              {/* Existing thread (newest first) */}
              <div className="space-y-2" data-testid="comments-thread">
                {(commentsApp?.comments?.length || 0) === 0 ? (
                  <p className="text-sm text-muted-foreground text-center py-4" data-testid="no-comments">
                    No comments yet. Be the first to add one.
                  </p>
                ) : (
                  [...commentsApp.comments].reverse().map((c) => {
                    const canDelete = isAdmin || c.by === (me.full_name || me.username);
                    return (
                      <div key={c.id || c.at} className="border border-slate-200 rounded-md p-2.5 bg-white" data-testid={`comment-${c.id || c.at}`}>
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex-1">
                            <p className="text-sm text-slate-900 whitespace-pre-wrap">{c.text}</p>
                            <p className="text-[11px] text-slate-500 mt-1">
                              <span className="font-medium text-slate-700">{c.by}</span>
                              {c.by_role && <span className="text-slate-400"> · {c.by_role}</span>}
                              <span className="text-slate-400"> · {c.at ? new Date(c.at).toLocaleString('en-ZA') : ''}</span>
                            </p>
                          </div>
                          {canDelete && c.id && (
                            <Button
                              size="sm"
                              variant="ghost"
                              className="h-6 w-6 p-0 text-rose-600 hover:bg-rose-50 shrink-0"
                              onClick={() => removeComment(c.id)}
                              data-testid={`delete-comment-${c.id}`}
                              title="Delete comment"
                            >
                              <Trash2 className="h-3 w-3" />
                            </Button>
                          )}
                        </div>
                      </div>
                    );
                  })
                )}
              </div>

              {/* Status history reference */}
              {(commentsApp?.status_history?.length || 0) > 0 && (
                <details className="text-xs">
                  <summary className="cursor-pointer text-slate-600 hover:text-slate-900">Status history ({commentsApp.status_history.length})</summary>
                  <ul className="mt-2 space-y-1 pl-2">
                    {[...commentsApp.status_history].reverse().map((h, i) => (
                      <li key={i} className="text-slate-600">
                        <span className={`inline-block px-1.5 py-0.5 rounded font-mono font-bold ${STATUS_COLOR[h.status] || ''}`}>{h.status}</span>
                        <span className="ml-2">{h.by}</span>
                        <span className="text-slate-400 ml-2">{h.at ? new Date(h.at).toLocaleString('en-ZA') : ''}</span>
                        {h.note && <p className="ml-7 text-slate-700">&ldquo;{h.note}&rdquo;</p>}
                      </li>
                    ))}
                  </ul>
                </details>
              )}
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}

// ---------- table sub-component ----------
function ApplicationTable({ loading, rows, type, onEdit, onDelete, onStatusChange, onComments, onOnboard, isAdmin }) {
  if (loading) return <p className="text-sm text-muted-foreground py-6 text-center">Loading…</p>;
  if (rows.length === 0) return <p className="text-sm text-muted-foreground py-6 text-center" data-testid="no-applications">No applications yet. Click <b>Add Application</b> to capture one.</p>;
  return (
    <div className="border rounded-md overflow-x-auto">
      <Table>
        <TableHeader className="bg-slate-50">
          <TableRow>
            <TableHead>Name</TableHead>
            <TableHead>Phone</TableHead>
            {type === 'driver' ? (
              <>
                <TableHead>Address</TableHead>
                <TableHead>Experience</TableHead>
              </>
            ) : (
              <>
                <TableHead>Vehicle Type</TableHead>
                <TableHead className="text-center">Ready for Training</TableHead>
              </>
            )}
            <TableHead>Status</TableHead>
            <TableHead>Captured</TableHead>
            <TableHead className="text-right">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map(a => (
            <TableRow key={a.id} data-testid={`application-row-${a.id}`}>
              <TableCell className="font-medium text-sm">
                <div className="flex items-center gap-2 flex-wrap">
                  <span>{a.first_name} {a.surname}</span>
                  {a.applying_for && (
                    <span
                      className={`inline-flex items-center text-[10px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded border ${APPLYING_FOR_BADGE[a.applying_for] || 'bg-slate-100 text-slate-700 border-slate-300'}`}
                      data-testid={`applying-for-${a.id}`}
                    >
                      {a.applying_for}
                    </span>
                  )}
                </div>
              </TableCell>
              <TableCell className="text-sm text-slate-700 font-mono">{a.phone || '—'}</TableCell>
              {type === 'driver' ? (
                <>
                  <TableCell className="text-xs text-slate-700 max-w-[180px] truncate" title={a.address}>{a.address || '—'}</TableCell>
                  <TableCell className="text-xs text-slate-700 max-w-[200px] truncate" title={a.experience}>{a.experience || '—'}</TableCell>
                </>
              ) : (
                <>
                  <TableCell className="text-sm text-slate-700">{a.vehicle_type || '—'}</TableCell>
                  <TableCell className="text-center">
                    {a.ready_for_training
                      ? <span className="inline-flex items-center text-xs font-bold text-emerald-700 bg-emerald-100 px-2 py-0.5 rounded">YES</span>
                      : <span className="inline-flex items-center text-xs text-slate-500 bg-slate-100 px-2 py-0.5 rounded">No</span>}
                  </TableCell>
                </>
              )}
              <TableCell>
                <Select value={a.status} onValueChange={(to) => to !== a.status && onStatusChange(a, to)}>
                  <SelectTrigger className={`h-7 w-40 text-xs font-bold border ${STATUS_COLOR[a.status] || ''}`} data-testid={`status-select-${a.id}`}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {STATUSES.map(s => (
                      <SelectItem key={s} value={s}>{STATUS_LABEL[s] || s}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </TableCell>
              <TableCell className="text-xs text-slate-600 whitespace-nowrap">
                {a.created_at ? new Date(a.created_at).toLocaleDateString('en-ZA') : '—'}
                <div className="text-[10px] text-slate-400">{a.created_by}</div>
              </TableCell>
              <TableCell className="text-right">
                <div className="flex gap-1 justify-end">
                  <Button size="sm" variant="ghost" className="h-7 px-2 text-xs relative" onClick={() => onComments(a)} data-testid={`comments-application-${a.id}`} title="View comments">
                    <MessageSquare className="h-3 w-3" />
                    {(a.comments?.length || 0) > 0 && (
                      <span className="ml-1 text-[10px] font-bold text-emerald-700">{a.comments.length}</span>
                    )}
                  </Button>
                  {isAdmin && !a.user_id && (a.status === 'HIRED' || a.status === 'AWAITING_DOCS' || a.status === 'INTERVIEWED' || a.status === 'SENT_TO_DPD') && (
                    <Button
                      size="sm"
                      className="h-7 px-2 text-xs bg-teal-600 hover:bg-teal-700 text-white"
                      onClick={() => onOnboard(a)}
                      data-testid={`onboard-application-${a.id}`}
                      title="Create driver login (awaiting training)"
                    >
                      <GraduationCap className="h-3 w-3 mr-1" /> Onboard
                    </Button>
                  )}
                  {a.user_id && (
                    <span className="inline-flex items-center gap-1 text-[10px] font-bold text-teal-700 bg-teal-50 px-1.5 py-0.5 rounded border border-teal-300" title="Driver login created">
                      <GraduationCap className="h-3 w-3" /> Onboarded
                    </span>
                  )}
                  <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => onEdit(a)} data-testid={`edit-application-${a.id}`}><Pencil className="h-3 w-3" /></Button>
                  {isAdmin && (
                    <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-rose-600 hover:bg-rose-50" onClick={() => onDelete(a)} data-testid={`delete-application-${a.id}`}><Trash2 className="h-3 w-3" /></Button>
                  )}
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

function CredentialRow({ label, value, onCopy, mono }) {
  return (
    <div className="flex items-center justify-between gap-2">
      <div className="text-xs text-slate-600 min-w-[80px]">{label}</div>
      <div className={`flex-1 text-sm text-slate-900 ${mono ? 'font-mono font-semibold' : 'font-medium'}`}>{value}</div>
      <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => onCopy(value)} title={`Copy ${label.toLowerCase()}`}>
        <Copy className="h-3 w-3" />
      </Button>
    </div>
  );
}

